/**
 * Triggers — คำที่ลูกค้าพิมพ์ → บอตจับ "เจตนา" → ทำ action (ส่งโปร/รูป/ตารางไซส์ ฯลฯ)
 * แก้คำที่นี่ → บอตจับเจตนาใหม่ทันที. ส่วน "ตอบว่าอะไร" ไปแก้ที่หน้าตามคอลัมน์ source
 *
 * ใช้ CrudManager กลาง — group ตามเจตนา + การ์ดอธิบายเจตนาเป็น headerExtra + เรียงด้วยเลขลำดับ
 */
import { useState } from 'react';
import { CrudManager, useBrandOptions, type CrudField } from '@/components/CrudManager';
import { IntentKeywordsApi, type IntentKeywordItem, type IntentType } from '@/lib/admin-api';

const INTENT_TYPES: { value: IntentType; label: string; emoji: string; effect: string }[] = [
  { value: 'greeting', label: 'ทักทาย', emoji: '👋', effect: 'ส่ง master promo reply (ราคา+โปร+สี+ไซส์) เลือกใหม่/เก่าอัตโนมัติ' },
  { value: 'promo', label: 'ถามราคา/โปร', emoji: '💰', effect: 'ส่ง master promo reply (ราคา+โปร+ของฟรี+CTA)' },
  { value: 'image_request', label: 'ขอดูรูป', emoji: '📸', effect: 'ส่งรูปหลัก (ถ้าระบุสี ส่งรูปสีนั้น)' },
  { value: 'size_chart', label: 'ตารางไซส์', emoji: '📏', effect: 'ส่งรูปตารางไซส์' },
  { value: 'thank_you', label: 'ขอบคุณ', emoji: '🙏', effect: 'reserved (ยังไม่ใช้)' },
  { value: 'complaint', label: 'ร้องเรียน', emoji: '⚠️', effect: 'reserved (อนาคต route ไปแอดมิน)' },
];
const INTENT_OPTS = INTENT_TYPES.map((t) => ({ value: t.value, label: `${t.emoji} ${t.label}` }));
const intentLabel = (v: string) => { const t = INTENT_TYPES.find((x) => x.value === v); return t ? `${t.emoji} ${t.label}` : v; };
const intentRank = (v: string) => INTENT_TYPES.findIndex((x) => x.value === v);

// adapter: IntentKeywordsApi.list รับ object → ปรับเป็น list(brand?) ของ CrudManager
const Api = {
  list: () => IntentKeywordsApi.list(),
  create: IntentKeywordsApi.create,
  update: IntentKeywordsApi.update,
  remove: IntentKeywordsApi.remove,
};

function IntentGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg p-3 bg-blue-50/40 text-sm">
      <button onClick={() => setOpen((v) => !v)} className="font-medium text-blue-800">ℹ️ แต่ละเจตนาทำอะไร {open ? '▲' : '▼'}</button>
      {open && (
        <ul className="mt-2 space-y-1 text-xs text-blue-900">
          {INTENT_TYPES.map((t) => (
            <li key={t.value}><b>{t.emoji} {t.label}</b> — {t.effect}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TriggersPage() {
  const brandOpts = useBrandOptions(true);
  const FIELDS: CrudField[] = [
    { key: 'intentType', label: 'เจตนา', type: 'select', required: true, options: INTENT_OPTS, default: 'greeting', help: 'ลูกค้าพิมพ์คำนี้แล้วบอตจะทำอะไร (ดู “แต่ละเจตนาทำอะไร”)' },
    { key: 'keyword', label: 'คำ/วลีที่ลูกค้าพิมพ์', type: 'text', required: true, placeholder: 'สวัสดี', help: 'บอตจับคำนี้ในข้อความลูกค้า → เข้าเจตนานี้' },
    { key: 'brand', label: 'แบรนด์', type: 'select', required: true, options: brandOpts, default: 'all', help: 'ใช้กับแบรนด์ไหน (ทุกแบรนด์ = ใช้ร่วม)' },
    { key: 'sortOrder', label: 'ลำดับ', type: 'number', default: 10, help: 'เลขน้อยขึ้นก่อน (จัดลำดับการตรวจ)' },
    { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
    { key: 'notes', label: 'หมายเหตุ', type: 'text', help: 'บันทึกภายใน' },
  ];
  return (
    <CrudManager<IntentKeywordItem>
      title="🎯 Triggers (คำ → เจตนา)"
      addLabel="เพิ่มคำ"
      help="ลูกค้าพิมพ์คำเหล่านี้ → บอตจับเจตนา → ทำ action. ส่วน “ตอบว่าอะไร” แก้ที่หน้าอื่น (Reply Templates/รูปสินค้า)"
      queryKey="intent-keywords"
      api={Api}
      fields={FIELDS}
      multiSelect
      searchKeys={['keyword', 'intentType']}
      groupBy={(r) => intentLabel(r.intentType)}
      sortRows={(a, b) => intentRank(a.intentType) - intentRank(b.intentType) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0)}
      rowTitle={(r) => <>{r.keyword}</>}
      rowSubtitle={(r) => <>แบรนด์: {r.brand}{r.notes ? ` · ${r.notes}` : ''}</>}
      headerExtra={<IntentGuide />}
    />
  );
}
